"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import Logo from "@/components/ui/icons/logo";
import BurgerMenu from "@/components/ui/icons/burger-menu";
import { usePathname } from "next/navigation";
import { X } from "lucide-react";
import CustomButton from "../ui/custom-button";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { LanguageSwitcher } from "./language-switcher";
import type { LocaleCode } from "@/i18n/locales";
interface NavItem {
  label: string;
  href: string;
  children?: NavItem[];
}

interface HeaderProps {
  className?: string;
  navItems?: NavItem[];
  /** Server-resolved locale passed from HeaderWrapper to seed LanguageSwitcher */
  initialLocale?: string;
}

export function Header({
  className,
  navItems,
  initialLocale = "en-US",
}: HeaderProps) {
  const t = useTranslations('header');

  // Build nav items from translations so labels update with locale
  const defaultNavItems: NavItem[] = [
    { label: t('locations'), href: "/locations" },
    {
      label: t('ourServices'),
      href: "#services",
      children: [
        { label: t('autoServices'), href: "/services/auto-services" },
        { label: t('collisionRepair'), href: "/services/collision-repair" },
      ],
    },
    {
      label: t('autoParts'),
      href: "#parts",
      children: [
        { label: t('requestParts'), href: "/parts/request" },
        { label: t('searchParts'), href: process.env.NEXT_PUBLIC_SEARCH_PARTS_URL || "/search-parts" },
      ],
    },
    {
      label: t('aboutUs'),
      href: "#about",
      children: [
        { label: t('whoWeAre'), href: "/about/us" },
        { label: t('blog'), href: "/about/blog" },
        { label: t('contact'), href: "/about/contact" },
      ],
    },
  ];

  const resolvedNavItems = navItems ?? defaultNavItems;
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  useEffect(() => {
    setOpenDropdown(null);
  }, [pathname]);

  const onClickOutside = () => {
    if (openDropdown) {
      setOpenDropdown(null);
    }
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        openDropdown &&
        !target.closest(".group") &&
        !target.closest(".burger-menu")
      ) {
        onClickOutside();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [openDropdown]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  const toggleDropdown = (label: string) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleRedirect = (href: string) => {
    router.push(href);
  };

  return (
    <>
      <header
        className={cn(
          " top-0 absolute left-0 right-0  p-4  border-b border-white/10 backdrop-blur bg-site/80",
          isMobileMenuOpen && "bg-transparent backdrop-blur-none border-transparent",
          className
        )}
      >
        <div className="  px-4 md:px-16 lg:px-20 flex items-center justify-between">
          <Link
            href="/" aria-label="Navigate to Home Page, open in same window"
            className={cn(
              "flex items-center",
              isMobileMenuOpen && "hidden md:flex"
            )}
          >
            <Logo width={180} height={45} />
          </Link>

          {/* Desktop Navigation - Full */}
          <nav id="navigation" role="navigation" aria-label="Main navigation" className="hidden xl:flex !z-[100] items-center space-x-8">
            {resolvedNavItems.map((item) => (
              <div key={item.label} className="relative group">
                {item.children ? (
                  <button
                    className="flex items-center capitalize text-site-foreground hover:text-yellow-500 transition-colors"
                  >
                    {item.label}
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </button>
                ) : (
                  <Link
                    href={item.href}
                    className="text-site-foreground capitalize hover:text-yellow-500 transition-colors"
                  >
                    {item.label}
                  </Link>
                )}

                {item.children &&  (
                  <div className="absolute left-0 mt-2 w-48 bg-zinc-900 rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        href={child.href}
                        className="block px-4 py-2 capitalize text-sm text-site-foreground hover:bg-zinc-800 hover:text-yellow-500"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop Navigation - Reduced (Large screens) */}
          <nav aria-label="Main navigation" className="hidden lg:flex xl:hidden !z-[100] items-center space-x-6">
            {resolvedNavItems.filter(item => item.href !== '#about').map((item) => (
              <div key={item.label} className="relative group">
                {item.children ? (
                  <button
                    className="flex items-center capitalize text-site-foreground hover:text-yellow-500 transition-colors text-sm"   
                  >
                    {item.label}
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </button>
                ) : (
                  <Link
                    href={item.href}
                    className="text-site-foreground capitalize hover:text-yellow-500 transition-colors text-sm"
                  >
                    {item.label}
                  </Link>
                )}

                {item.children && (
                  <div className="absolute left-0 mt-2 w-48 bg-zinc-900 rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        href={child.href}
                        className="block px-4 py-2 capitalize text-sm text-site-foreground hover:bg-zinc-800 hover:text-yellow-500"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop Navigation - Minimal (Medium screens) */}
          <nav aria-label="Main navigation" className="hidden md:flex lg:hidden !z-[100] items-center space-x-4">
            {resolvedNavItems.filter(item =>
              item.href === '/locations' || item.href === '#parts'
            ).map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className="text-site-foreground capitalize hover:text-yellow-500 transition-colors text-sm whitespace-nowrap"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right side — language switcher + book service grouped to preserve nav centering */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher initialLocale={initialLocale as LocaleCode} />
            <CustomButton
              size="sm"
              onClick={() => handleRedirect("/about/contact")}
            >
              {t('bookService')}
            </CustomButton>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-site-foreground hover:text-yellow-500 transition-colors"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {!isMobileMenuOpen && <BurgerMenu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={closeMobileMenu}
          />

          {/* Mobile Menu */}
          <div className="fixed top-0 right-0 h-full w-screen bg-zinc-900 shadow-xl transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-between p-6 border-b border-zinc-800">
                <Logo width={140} height={35} />
                <button
                  onClick={closeMobileMenu}
                  className="text-site-foreground transition-colors"
                  aria-label="Close mobile menu"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Mobile Navigation */}
              <nav aria-label="Mobile navigation" className="flex-1 z-20 overflow-y-auto py-6">
                {resolvedNavItems.map((item) => (
                  <div key={item.label} className=" px-6">
                    {item.children ? (
                      <div className="">
                        <button
                          className="flex items-center justify-between w-full text-left text-site-foreground hover:text-yellow-500 transition-colors py-3"
                          onClick={() => toggleDropdown(item.label)}
                        >
                          <span className="text-lg font-medium">
                            {item.label}
                          </span>
                          <ChevronDown
                            className={cn(
                              "h-5 w-5 transition-transform duration-200",
                              openDropdown === item.label && "rotate-180"
                            )}
                          />
                        </button>
                        {openDropdown === item.label && (
                          <div className="ml-4 mt-2 space-y-2">
                            {item.children.map((child) => (
                              <Link
                                key={child.label}
                                href={child.href}
                                className="block text-site-foreground/70 hover:text-yellow-500 transition-colors py-2"
                                onClick={closeMobileMenu}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        href={item.href}
                        className="block text-site-foreground hover:text-yellow-500 transition-colors py-3 text-lg font-medium"
                        onClick={closeMobileMenu}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>

              {/* Mobile Book Service Button */}
              <div className="p-6 border-t border-zinc-800 flex flex-col gap-4">
                {/* Language switcher — mobile */}
                <div className="flex justify-center">
                  <LanguageSwitcher initialLocale={initialLocale as LocaleCode} />
                </div>
                <CustomButton
                  size="md"
                  className="w-full"
                  onClick={() => {
                    handleRedirect("/about/contact")
                    setIsMobileMenuOpen(false)
                  }}
                >
                  {t('bookService')}
                </CustomButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
